import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { MousePointerClick, PhoneCall, DollarSign, Mail, Check, ChevronLeft, ChevronRight, Globe, Smartphone } from 'lucide-react';
import { SourceLogo, TrafficSourceLogo } from '../SourceLogo/SourceLogo';
import { useRevit } from '../../contexts/RevitContext';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { fetchOffers } from '../../utils/apiCalls';
import { applyDashboardTimeframe, queryRangeIso } from '../../utils/dateRange';
import Icon from '../Icon';
import 'react-datepicker/dist/react-datepicker.css';
import './Audience.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const WORLD_GEOJSON_URL = '/geo/countries.geo.json';

const MAP_WIDTH = 960;
const MAP_HEIGHT = 500;

const LEADS_PER_PAGE = 25;
const LEAD_SEARCH_DEBOUNCE_MS = 300;
const JOURNEY_EMAILS_COLLAPSED = 5;

const EVENT_TYPES = [
    { id: 'clicks', label: 'Traffic', Icon: MousePointerClick },
    { id: 'calls', label: 'Calls', Icon: PhoneCall },
    { id: 'emails', label: 'Emails', Icon: Mail },
    { id: 'sales', label: 'Sales', Icon: DollarSign },
];

const EVENT_NOUN = {
    clicks: { singular: 'visit', plural: 'visits' },
    calls: { singular: 'call', plural: 'calls' },
    sales: { singular: 'sale', plural: 'sales' },
    emails: { singular: 'email', plural: 'emails' },
};

const BREAKDOWN_MODES = [
    { id: 'source', label: 'Traffic source', Icon: Globe },
    { id: 'device', label: 'Device', Icon: Smartphone },
];


const project = ([lon, lat]) => [
    ((lon + 180) / 360) * MAP_WIDTH,
    ((90 - lat) / 180) * MAP_HEIGHT,
];

const ringToPath = (ring) => {
    if (!ring?.length) return '';
    return ring.map((coord, i) => {
        const [x, y] = project(coord);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(' ') + 'Z';
};

const geometryToPath = (geometry) => {
    if (!geometry) return '';
    if (geometry.type === 'Polygon') {
        return geometry.coordinates.map(ringToPath).join(' ');
    }
    if (geometry.type === 'MultiPolygon') {
        return geometry.coordinates
            .map((polygon) => polygon.map(ringToPath).join(' '))
            .join(' ');
    }
    return '';
};

const lerp = (a, b, t) => a + (b - a) * t;

const choroplethFill = (count, max) => {
    if (!count || !max) return null;
    const t = Math.log(count + 1) / Math.log(max + 1);
    const r = Math.round(lerp(8, 0, t));
    const g = Math.round(lerp(58, 211, t));
    const b = Math.round(lerp(68, 242, t));
    return `rgb(${r}, ${g}, ${b})`;
};

const formatCompactValue = (amount, currency) => {
    const value = Number(amount) || 0;
    const formatted = value.toLocaleString(undefined, {
        maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    });
    return `${getCurrencySymbol(currency)}${formatted}`;
};

const formatSaleBadge = (amount, currency) => `${formatCompactValue(amount, currency)}`;

const conversionTone = (rate) => {
    if (rate >= 5) return 'good';
    if (rate > 0 && rate < 3) return 'warn';
    return 'neutral';
};

const formatPercent = (rate) => `${(Number(rate) || 0).toFixed(1)}%`;

/* Stacks multi-word column headers ("Sales conversion") on two lines to keep columns narrow */
const renderTrafficHeaderLabel = (label) => {
    const words = String(label || '').trim().split(/\s+/);
    if (words.length < 2) return label;
    return (
        <span className="audience-th-stack">
            <span>{words[0]}</span>
            <span>{words.slice(1).join(' ')}</span>
        </span>
    );
};

const TRAFFIC_COLUMN_STORAGE_KEY = 'audienceTrafficVisibleColumns';

const TRAFFIC_COLUMNS = [
    { id: 'views', label: 'Views', defaultVisible: true },
    { id: 'clicks', label: 'Traffic', defaultVisible: true },
    { id: 'visitors', label: 'Visitors', defaultVisible: true },
    { id: 'leads', label: 'Email Signups', defaultVisible: true },
    { id: 'emailConversion', label: 'Email conversion', defaultVisible: false },
    { id: 'calls', label: 'Calls', defaultVisible: false },
    { id: 'callsConversion', label: 'Calls conversion', defaultVisible: false },
    { id: 'customers', label: 'Customers', defaultVisible: true },
    { id: 'conversion', label: 'Sales conversion', defaultVisible: true },
    { id: 'avgValue', label: 'Avg. Value', defaultVisible: false },
    { id: 'revenue', label: 'Total revenue', defaultVisible: true },
    { id: 'quality', label: 'Quality', defaultVisible: true },
];

const TRAFFIC_COLUMN_DEFAULTS = Object.fromEntries(
    TRAFFIC_COLUMNS.map((column) => [column.id, column.defaultVisible])
);

const funnelStepLabel = (step, nameCounts) => {
    const name = step.name || 'Funnel step';
    if ((nameCounts[name] || 0) > 1 && step.offerName) return `${name} (${step.offerName})`;
    return name;
};

const buildTrafficColumns = (funnelSteps) => {
    const steps = Array.isArray(funnelSteps) ? funnelSteps : [];
    const nameCounts = {};
    steps.forEach((step) => {
        const name = step.name || 'Funnel step';
        nameCounts[name] = (nameCounts[name] || 0) + 1;
    });
    const funnelColumns = steps.flatMap((step) => {
        const label = funnelStepLabel(step, nameCounts);
        const stepId = String(step.id);
        return [
            {
                id: `funnel:${stepId}`,
                label,
                defaultVisible: false,
                kind: 'funnel',
                stepId,
            },
            {
                id: `funnelRate:${stepId}`,
                label: `${label} conversion`,
                defaultVisible: false,
                kind: 'funnelRate',
                stepId,
            },
        ];
    });
    if (funnelColumns.length === 0) return TRAFFIC_COLUMNS;
    const columns = [];
    TRAFFIC_COLUMNS.forEach((column) => {
        columns.push(column);
        if (column.id === 'callsConversion') columns.push(...funnelColumns);
    });
    return columns;
};

const loadTrafficColumns = () => {
    try {
        const saved = localStorage.getItem(TRAFFIC_COLUMN_STORAGE_KEY);
        if (!saved) return { ...TRAFFIC_COLUMN_DEFAULTS };
        const parsed = JSON.parse(saved);
        const merged = { ...TRAFFIC_COLUMN_DEFAULTS, ...parsed };
        if (!Object.prototype.hasOwnProperty.call(parsed, 'revenue')) {
            merged.avgValue = TRAFFIC_COLUMN_DEFAULTS.avgValue;
        }
        if (!Object.prototype.hasOwnProperty.call(parsed, 'clicks')) {
            merged.clicks = TRAFFIC_COLUMN_DEFAULTS.clicks;
        }
        return merged;
    } catch (error) {
        return { ...TRAFFIC_COLUMN_DEFAULTS };
    }
};

const emailInitials = (email) => {
    if (!email) return '?';
    const [local = '', domain = ''] = email.split('@');
    const first = local[0] || '?';
    const second = domain[0] || local[1] || first;
    return `${first}${second}`.toUpperCase();
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const leadHasEmail = (lead) => Boolean(lead?.email && String(lead.email).trim());

const leadHasName = (lead) => Boolean(lead?.name && String(lead.name).trim());

const leadLabel = (lead) => {
    if (leadHasEmail(lead)) return lead.email;
    if (leadHasName(lead)) return lead.name;
    return lead?.id || '—';
};

const leadInitials = (lead) => {
    if (leadHasEmail(lead)) return emailInitials(lead.email);
    if (leadHasName(lead)) {
        const parts = String(lead.name).trim().split(/\s+/);
        const first = parts[0]?.[0] || '';
        const second = parts[1]?.[0] || parts[0]?.[1] || first;
        return `${first}${second}`.toUpperCase();
    }
    const compact = String(lead?.id || '').replace(/[^a-zA-Z0-9]/g, '');
    return (compact.slice(0, 2) || '?').toUpperCase();
};

// Portaled to <body>: the lead list scrolls (would clip it) and transformed ancestors would offset position: fixed
const LeadEmailsBadge = ({ emails }) => {
    const [position, setPosition] = useState(null);
    const hideTimer = useRef(null);
    const label = `${emails.length} more email${emails.length === 1 ? '' : 's'}`;

    useEffect(() => () => clearTimeout(hideTimer.current), []);

    const show = (event) => {
        clearTimeout(hideTimer.current);
        const rect = event.currentTarget.getBoundingClientRect();
        setPosition({ left: rect.right + 8, top: rect.top + rect.height / 2 });
    };
    const keepOpen = () => clearTimeout(hideTimer.current);
    const hide = () => {
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setPosition(null), 120);
    };

    return (
        <span
            className="audience-lead-email-count"
            onMouseEnter={show}
            onMouseLeave={hide}
            aria-label={`${label}: ${emails.join(', ')}`}
        >
            +{emails.length}
            {position && createPortal(
                <span
                    className="audience-lead-email-tooltip"
                    role="tooltip"
                    style={{ left: position.left, top: position.top }}
                    onMouseEnter={keepOpen}
                    onMouseLeave={hide}
                >
                    <span className="audience-lead-email-tooltip-title">{label}</span>
                    {emails.map((email) => (
                        <span key={email} className="audience-lead-email-tooltip-item">{email}</span>
                    ))}
                </span>,
                document.body
            )}
        </span>
    );
};

const formatEventTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return 'Unknown time';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const featureIso3 = (feature) => {
    const raw = feature.id || feature.properties?.ISO_A3 || feature.properties?.iso_a3 || '';
    return String(raw).toUpperCase();
};

const Audience = () => {
    const { username } = useRevit();
    const { getToken } = useAuth();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const leadFromUrl = searchParams.get('lead');
    const intelRef = useRef(null);
    const [hovered, setHovered] = useState(null);
    const [eventType, setEventType] = useState('clicks');
    const [trafficBreakdown, setTrafficBreakdown] = useState('source');
    const [timeframeOption, setTimeframeOption] = useState('30days');
    const [customStartDate, setCustomStartDate] = useState(() => applyDashboardTimeframe('30days').startDate);
    const [customEndDate, setCustomEndDate] = useState(() => applyDashboardTimeframe('30days').endDate);
    const [selectedLeadId, setSelectedLeadId] = useState(null);
    const [leadSearch, setLeadSearch] = useState('');
    const [debouncedLeadSearch, setDebouncedLeadSearch] = useState('');
    const [leadPage, setLeadPage] = useState(1);
    const [showAllJourneyEmails, setShowAllJourneyEmails] = useState(false);
    const [emailsOnly, setEmailsOnly] = useState(false);
    const [connectingLeadId, setConnectingLeadId] = useState(null);
    const [journeyConnectField, setJourneyConnectField] = useState(null);
    const [connectEmail, setConnectEmail] = useState('');
    const [connectName, setConnectName] = useState('');
    const [connectError, setConnectError] = useState('');
    const [connectSaving, setConnectSaving] = useState(false);
    const [selectedOfferId, setSelectedOfferId] = useState('');
    const [visibleTrafficColumns, setVisibleTrafficColumns] = useState(loadTrafficColumns);
    const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
    const columnSelectorRef = useRef(null);

    const persistTrafficColumns = (nextColumns) => {
        localStorage.setItem(TRAFFIC_COLUMN_STORAGE_KEY, JSON.stringify(nextColumns));
        setVisibleTrafficColumns(nextColumns);
    };

    const toggleTrafficColumn = (columnId) => {
        persistTrafficColumns({
            ...visibleTrafficColumns,
            [columnId]: !visibleTrafficColumns[columnId],
        });
    };

    const resetTrafficColumns = () => {
        persistTrafficColumns({ ...TRAFFIC_COLUMN_DEFAULTS });
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isColumnSelectorOpen && columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
                setIsColumnSelectorOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isColumnSelectorOpen]);

    const handleTimeframeChange = (option) => {
        setTimeframeOption(option);
        if (option !== 'custom') {
            const { startDate, endDate } = applyDashboardTimeframe(option);
            setCustomStartDate(startDate);
            setCustomEndDate(endDate);
        }
    };

    const { start: queryStart, end: queryEnd } = useMemo(
        () => queryRangeIso(customStartDate, customEndDate),
        [customStartDate, customEndDate]
    );

    const { data, error, isFetching } = useQuery({
        queryKey: ['audienceGeo', username, eventType, queryStart, queryEnd, selectedOfferId],
        queryFn: async () => {
            const token = await getToken();
            const response = await axios.get(`https://${SERVER_URL}/api/audience-geo/${username}`, {
                params: {
                    eventType,
                    startDate: queryStart,
                    endDate: queryEnd,
                    ...(selectedOfferId ? { offer: selectedOfferId, offerId: selectedOfferId } : {}),
                },
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        },
        enabled: !!username,
        staleTime: 5 * 60 * 1000,
    });

    const {
        data: leadsData,
        error: leadsError,
        isFetching: leadsFetching,
    } = useQuery({
        queryKey: ['audienceLeads', username, emailsOnly, debouncedLeadSearch, leadPage],
        queryFn: async () => {
            const token = await getToken();
            const response = await axios.get(`https://${SERVER_URL}/api/audience-leads/${username}`, {
                params: {
                    page: leadPage,
                    pageSize: LEADS_PER_PAGE,
                    ...(debouncedLeadSearch ? { search: debouncedLeadSearch } : {}),
                    ...(emailsOnly ? { emailsOnly: '1' } : {}),
                },
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        },
        enabled: !!username,
        staleTime: 5 * 60 * 1000,
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedLeadSearch(leadSearch.trim());
            setLeadPage(1);
        }, LEAD_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [leadSearch]);

    const {
        data: journey,
        error: journeyError,
        isLoading: journeyFetching,
    } = useQuery({
        queryKey: ['audienceJourney', username, selectedLeadId],
        queryFn: async () => {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/audience-journey/${username}/${encodeURIComponent(selectedLeadId)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data;
        },
        enabled: !!username && !!selectedLeadId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: offersData } = useQuery({
        queryKey: ['audienceOffers', username],
        queryFn: async () => {
            const token = await getToken();
            return fetchOffers(username, token);
        },
        enabled: !!username,
        staleTime: 5 * 60 * 1000,
    });

    const {
        data: trafficData,
        error: trafficError,
        isFetching: trafficFetching,
    } = useQuery({
        queryKey: ['audienceTrafficSources', username, queryStart, queryEnd, selectedOfferId],
        queryFn: async () => {
            const token = await getToken();
            const response = await axios.get(`https://${SERVER_URL}/api/audience-traffic-sources/${username}`, {
                params: {
                    startDate: queryStart,
                    endDate: queryEnd,
                    ...(selectedOfferId ? { offer: selectedOfferId, offerId: selectedOfferId } : {}),
                },
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log('[Audience] traffic sources response', {
                sourceCount: response.data?.sources?.length ?? 0,
                sourceIds: (response.data?.sources || []).map((source) => source.id),
                instagram: (response.data?.sources || []).find((source) => source.id === 'instagram') || null,
                youtube: (response.data?.sources || []).find((source) => source.id === 'youtube') || null,
                data: response.data,
            });
            return response.data;
        },
        enabled: !!username,
        staleTime: 5 * 60 * 1000,
    });

    const { data: world, isLoading: worldLoading, error: worldError } = useQuery({
        queryKey: ['worldGeojson'],
        queryFn: async () => {
            const response = await fetch(WORLD_GEOJSON_URL);
            if (!response.ok) throw new Error('Failed to load world map');
            return response.json();
        },
        staleTime: Infinity,
    });

    const countries = useMemo(() => data?.countries || [], [data]);
    const maxCount = countries[0]?.count || 0;
    const byIso3 = useMemo(() => {
        const map = {};
        countries.forEach((country) => {
            if (country.iso3) map[country.iso3] = country;
        });
        return map;
    }, [countries]);

    const features = useMemo(() => {
        if (!world?.features) return [];
        return world.features
            .filter((feature) => {
                const iso3 = featureIso3(feature);
                const name = feature.properties?.name || '';
                return iso3 !== 'ATA' && name !== 'Antarctica';
            })
            .map((feature) => ({
                iso3: featureIso3(feature),
                name: feature.properties?.name || featureIso3(feature),
                d: geometryToPath(feature.geometry),
            }))
            .filter((feature) => feature.d);
    }, [world]);

    const noun = EVENT_NOUN[eventType];
    const locatedEvents = data?.locatedEvents || 0;
    const totalEvents = data?.totalEvents || 0;
    const isMapLoading = isFetching || worldLoading;
    const showEmpty = !isMapLoading && !error && totalEvents === 0;
    const leads = leadsData?.leads || [];
    const totalLeads = leadsData?.total ?? 0;
    const totalLeadPages = Math.max(1, Math.ceil(totalLeads / LEADS_PER_PAGE));
    const currentLeadPage = Math.min(leadPage, totalLeadPages);
    const isSearchingLeads = Boolean(debouncedLeadSearch);
    const currency = leadsData?.currency || 'USD';
    const trafficSources = trafficData?.sources || [];
    const trafficDevices = trafficData?.devices || [];
    const trafficRows = trafficBreakdown === 'device' ? trafficDevices : trafficSources;
    const trafficCurrency = trafficData?.currency || currency;
    const trafficColumns = useMemo(
        () => buildTrafficColumns(trafficData?.funnelSteps),
        [trafficData?.funnelSteps]
    );
    const visibleTrafficColumnList = trafficColumns.filter((column) => visibleTrafficColumns[column.id]);

    useEffect(() => {
        if (!leadFromUrl) return;
        setSelectedLeadId(leadFromUrl);
        setEmailsOnly(false);
        const frame = requestAnimationFrame(() => {
            intelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return () => cancelAnimationFrame(frame);
    }, [leadFromUrl]);

    useEffect(() => {
        if (leadsData && leadsData.leads.length === 0 && leadPage > 1) {
            setLeadPage(1);
        }
    }, [leadsData, leadPage]);

    useEffect(() => {
        setConnectEmail('');
        setConnectName('');
        setConnectError('');
        setConnectSaving(false);
    }, [connectingLeadId, journeyConnectField]);

    useEffect(() => {
        setJourneyConnectField(null);
        setShowAllJourneyEmails(false);
    }, [selectedLeadId]);

    const handleConnectLead = async (event, leadId, mode = 'both') => {
        event.preventDefault();
        if (!leadId || connectSaving) return;

        const email = mode === 'name' ? '' : connectEmail.trim().toLowerCase();
        const name = mode === 'email' ? '' : connectName.trim().slice(0, 200);

        if (mode === 'email') {
            if (!email) {
                setConnectError('Enter an email.');
                return;
            }
            if (!EMAIL_PATTERN.test(email)) {
                setConnectError('Enter a valid email.');
                return;
            }
        } else if (mode === 'name') {
            if (!name) {
                setConnectError('Enter a name.');
                return;
            }
        } else if (!email && !name) {
            setConnectError('Enter an email or a name.');
            return;
        } else if (email && !EMAIL_PATTERN.test(email)) {
            setConnectError('Enter a valid email.');
            return;
        }

        setConnectSaving(true);
        setConnectError('');
        try {
            const token = await getToken();
            const headers = { Authorization: `Bearer ${token}` };
            let currentLeadId = leadId;
            if (email) {
                const response = await axios.post(
                    `https://${SERVER_URL}/api/audience-leads/${username}/${encodeURIComponent(leadId)}/email`,
                    { email },
                    { headers }
                );
                // Adding an email can merge this lead into an older lead with the same email
                currentLeadId = response.data?.leadId || leadId;
            }
            if (name) {
                await axios.post(
                    `https://${SERVER_URL}/api/audience-leads/${username}/${encodeURIComponent(currentLeadId)}/add-name`,
                    { name },
                    { headers }
                );
            }
            setConnectEmail('');
            setConnectName('');
            setConnectingLeadId(null);
            setJourneyConnectField(null);
            if (currentLeadId !== leadId && selectedLeadId === leadId) {
                setSelectedLeadId(currentLeadId);
            }
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['audienceLeads', username] }),
                queryClient.invalidateQueries({ queryKey: ['audienceJourney', username, currentLeadId] }),
            ]);
        } catch (error) {
            setConnectError(error.response?.data?.error || 'Could not save email or name.');
        } finally {
            setConnectSaving(false);
        }
    };

    const funnelStepConversions = (source, stepId) => source.funnelSteps?.[stepId] ?? 0;

    const funnelStepRate = (source, stepId) => {
        const visitors = source.visitors || 0;
        if (visitors <= 0) return 0;
        return (funnelStepConversions(source, stepId) / visitors) * 100;
    };

    const renderTrafficCell = (column, source) => {
        const columnId = column.id;
        const emailConversion = source.emailConversion ?? (source.visitors > 0 ? (source.leads / source.visitors) * 100 : 0);
        const callsConversion = source.callsConversion ?? (source.visitors > 0 ? ((source.calls ?? 0) / source.visitors) * 100 : 0);
        if (column.kind === 'funnel') return funnelStepConversions(source, column.stepId).toLocaleString();
        if (column.kind === 'funnelRate') return formatPercent(funnelStepRate(source, column.stepId));
        if (columnId === 'views') return source.views == null ? '—' : source.views.toLocaleString();
        if (columnId === 'clicks') return (source.clicks ?? 0).toLocaleString();
        if (columnId === 'visitors') return source.visitors.toLocaleString();
        if (columnId === 'leads') return source.leads.toLocaleString();
        if (columnId === 'calls') return (source.calls ?? 0).toLocaleString();
        if (columnId === 'customers') return source.customers.toLocaleString();
        if (columnId === 'emailConversion') return formatPercent(emailConversion);
        if (columnId === 'callsConversion') return formatPercent(callsConversion);
        if (columnId === 'conversion') return formatPercent(source.conversion);
        if (columnId === 'revenue') return formatCompactValue(source.revenue, trafficCurrency);
        if (columnId === 'avgValue') return formatCompactValue(source.avgValue, trafficCurrency);
        if (columnId === 'quality') {
            return (
                <span className={`audience-traffic-quality is-${source.qualityTone || 'neutral'}`}>
                    {source.quality}
                </span>
            );
        }
        return '—';
    };

    const trafficCellClassName = (column, source) => {
        const columnId = column.id;
        const emailConversion = source.emailConversion ?? (source.visitors > 0 ? (source.leads / source.visitors) * 100 : 0);
        const callsConversion = source.callsConversion ?? (source.visitors > 0 ? ((source.calls ?? 0) / source.visitors) * 100 : 0);
        if (column.kind === 'funnelRate') return `audience-traffic-conversion is-${conversionTone(funnelStepRate(source, column.stepId))}`;
        if (columnId === 'emailConversion') return `audience-traffic-conversion is-${conversionTone(emailConversion)}`;
        if (columnId === 'callsConversion') return `audience-traffic-conversion is-${conversionTone(callsConversion)}`;
        if (columnId === 'conversion') return `audience-traffic-conversion is-${conversionTone(source.conversion)}`;
        return undefined;
    };
    const offers = (offersData?.offers || []).filter((offer) => !offer.is_archived);
    const selectedLead = leads.find((lead) => lead.id === selectedLeadId);
    const journeyEvents = journey?.events || [];
    const journeyEmails = journey?.emails || selectedLead?.emails || [];
    const journeyEmail = journey?.email || selectedLead?.email || '';
    const visibleJourneyEmails = showAllJourneyEmails
        ? journeyEmails
        : journeyEmails.slice(0, JOURNEY_EMAILS_COLLAPSED);
    const journeyName = journey?.name || selectedLead?.name || '';
    const journeyHasEmail = Boolean(String(journeyEmail).trim());
    const openJourneyEmailForm = () => {
        setConnectingLeadId(null);
        setJourneyConnectField('email');
    };
    const journeyEmailForm = (
        <form
            className="audience-journey-connect-form"
            onSubmit={(event) => handleConnectLead(event, selectedLeadId, 'email')}
        >
            <input
                type="email"
                className="audience-add-email-input"
                value={connectEmail}
                onChange={(event) => setConnectEmail(event.target.value)}
                placeholder="Email"
                aria-label="Email"
                autoFocus
            />
            <button
                type="submit"
                className="audience-add-email-submit"
                disabled={connectSaving || !connectEmail.trim()}
            >
                {connectSaving ? 'Sending…' : 'Send'}
            </button>
            <button
                type="button"
                className="audience-lead-connect-cancel"
                onClick={() => setJourneyConnectField(null)}
                disabled={connectSaving}
            >
                Cancel
            </button>
        </form>
    );
    const journeyHasName = Boolean(String(journeyName).trim());
    const journeyValue = journey?.value ?? 0;
    const journeyCurrency = journey?.currency || currency;
    const journeySource = journey?.source || null;

    return (
        <div className="audience-container">
            <div className="audience-header-row">
                <p className="body-large-semibold">Audience</p>
            </div>

            <div className="audience-controls">
                <div className="audience-shared-filters">
                    <div className="audience-shared-filters-row">
                        <div className="audience-control">
                            <label htmlFor="audience-timeframe">Date range</label>
                            <div className="audience-timeframe">
                                <select
                                    id="audience-timeframe"
                                    value={timeframeOption}
                                    onChange={(e) => handleTimeframeChange(e.target.value)}
                                    className="audience-select"
                                >
                                    <option value="30days">Last 30 Days</option>
                                    <option value="monthtodate">Month to Date</option>
                                    <option value="lastmonth">Previous Month</option>
                                    <option value="90days">Last 90 Days</option>
                                    <option value="6months">Last 6 Months</option>
                                    <option value="1year">Last 1 Year</option>
                                    <option value="yeartodate">Year to Date</option>
                                    <option value="custom">Custom</option>
                                </select>
                                {timeframeOption === 'custom' && (
                                    <div className="audience-date-range">
                                        <DatePicker
                                            selected={customStartDate}
                                            onChange={(date) => setCustomStartDate(date)}
                                            selectsStart
                                            startDate={customStartDate}
                                            endDate={customEndDate}
                                            placeholderText="Start Date"
                                            dateFormat="MMM d, yyyy"
                                            maxDate={new Date()}
                                        />
                                        <span className="audience-date-separator">to</span>
                                        <DatePicker
                                            selected={customEndDate}
                                            onChange={(date) => setCustomEndDate(date)}
                                            selectsEnd
                                            startDate={customStartDate}
                                            endDate={customEndDate}
                                            minDate={customStartDate}
                                            maxDate={new Date()}
                                            placeholderText="End Date"
                                            dateFormat="MMM d, yyyy"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {offers.length > 0 && (
                            <div className="audience-control">
                                <label htmlFor="audience-offer">Offer</label>
                                <select
                                    id="audience-offer"
                                    className="audience-select"
                                    value={selectedOfferId}
                                    onChange={(event) => setSelectedOfferId(event.target.value)}
                                >
                                    <option value="">All offers</option>
                                    {offers.map((offer) => (
                                        <option key={offer.id} value={offer.id}>{offer.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {error && <p className="audience-status audience-status--error">Could not load audience data.</p>}

            <div className="audience-layout">
                <div className={`audience-geo-card${isFetching ? ' is-loading' : ''}`}>
                    <div className="audience-geo-header">
                        <div className="audience-map-toolbar">
                            <span className="audience-control-label">Event type</span>
                            <div className="audience-event-types" role="tablist" aria-label="Event type">
                                {EVENT_TYPES.map(({ id, label, Icon }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        role="tab"
                                        aria-selected={eventType === id}
                                        className={`audience-event-type${eventType === id ? ' active' : ''}`}
                                        onClick={() => setEventType(id)}
                                    >
                                        <Icon size={14} />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <p className="body-medium-semibold audience-list-title">Top countries</p>
                    </div>
                    <div className="audience-geo-body">
                        <div className="audience-map-card">
                    {worldError && (
                        <p className="audience-status audience-status--error">
                            Could not load the world map. Country list is still available.
                        </p>
                    )}
                    {showEmpty && (
                        <p className="audience-status">
                            No {noun.plural} with lead IPs in this period.
                        </p>
                    )}
                    {(features.length > 0 || isMapLoading) && !showEmpty && (
                        <div className={`audience-map-wrap${isMapLoading ? ' is-loading' : ''}`}>
                            {features.length > 0 && (
                                <svg
                                    className="audience-map"
                                    viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
                                    role="img"
                                    aria-label={`${noun.plural} by country`}
                                    aria-busy={isMapLoading}
                                >
                                    {features.map((feature) => {
                                        const country = byIso3[feature.iso3];
                                        const fill = choroplethFill(country?.count, maxCount);
                                        const isHovered = !isMapLoading && hovered?.iso3 === feature.iso3;
                                        return (
                                            <path
                                                key={feature.iso3 || feature.name}
                                                d={feature.d}
                                                fill={fill || 'var(--Elevations-Level2)'}
                                                stroke={isHovered ? 'var(--Primary-500)' : 'var(--Stroke-Light)'}
                                                strokeWidth={isHovered ? 1.4 : 0.5}
                                                className="audience-country"
                                                onMouseEnter={(event) => {
                                                    if (isMapLoading) return;
                                                    setHovered({
                                                        iso3: feature.iso3,
                                                        name: country?.name || feature.name,
                                                        count: country?.count || 0,
                                                        x: event.clientX,
                                                        y: event.clientY,
                                                    });
                                                }}
                                                onMouseMove={(event) => {
                                                    if (isMapLoading) return;
                                                    setHovered((prev) => prev && ({
                                                        ...prev,
                                                        x: event.clientX,
                                                        y: event.clientY,
                                                    }));
                                                }}
                                                onMouseLeave={() => setHovered(null)}
                                            />
                                        );
                                    })}
                                </svg>
                            )}
                            {isMapLoading && (
                                <div className="audience-map-overlay" role="status">
                                    <span>Loading audience map…</span>
                                </div>
                            )}
                            <div className="audience-legend">
                                <span>Fewer {noun.plural}</span>
                                <span className="audience-legend-bar" />
                                <span>More {noun.plural}</span>
                            </div>
                        </div>
                    )}
                    {hovered && !isMapLoading && (
                        <div
                            className="audience-tooltip"
                            style={{ top: hovered.y + 12, left: hovered.x + 12 }}
                        >
                            <strong>{hovered.name}</strong>
                            <span>
                                {hovered.count.toLocaleString()} {hovered.count === 1 ? noun.singular : noun.plural}
                                {locatedEvents > 0 && hovered.count > 0
                                    ? ` · ${((hovered.count / locatedEvents) * 100).toFixed(1)}%`
                                    : ''}
                            </span>
                        </div>
                    )}
                </div>

                <div className="audience-list-card">
                    {isFetching && (
                        <p className="audience-list-loading" role="status">Loading…</p>
                    )}
                    {!isFetching && countries.length === 0 ? (
                        <p className="audience-status">No countries could be resolved from stored IPs.</p>
                    ) : (
                        countries.length > 0 && (
                            <ol className="audience-list">
                                {countries.map((country) => (
                                    <li key={country.code}>
                                        <span className="audience-list-name">{country.name}</span>
                                        <span className="audience-list-bar-wrap">
                                            <span
                                                className="audience-list-bar"
                                                style={{ width: `${maxCount ? (country.count / maxCount) * 100 : 0}%` }}
                                            />
                                        </span>
                                        <span className="audience-list-count">{country.count.toLocaleString()}</span>
                                    </li>
                                ))}
                            </ol>
                        )
                    )}
                </div>
                    </div>
                </div>

                <div className={`audience-traffic-card${trafficFetching ? ' is-loading' : ''}`}>
                    <div className="audience-traffic-header">
                        <div className="audience-traffic-header-main">
                            <p className="body-medium-semibold audience-list-title">
                                {trafficBreakdown === 'device' ? 'By device' : 'By traffic source'}
                            </p>
                            <div className="audience-event-types" role="tablist" aria-label="Breakdown">
                                {BREAKDOWN_MODES.map(({ id, label, Icon }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        role="tab"
                                        aria-selected={trafficBreakdown === id}
                                        className={`audience-event-type${trafficBreakdown === id ? ' active' : ''}`}
                                        onClick={() => setTrafficBreakdown(id)}
                                    >
                                        <Icon size={14} />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="audience-column-selector" ref={columnSelectorRef}>
                            <button
                                type="button"
                                className={`audience-column-toggle${isColumnSelectorOpen ? ' is-open' : ''}`}
                                onClick={() => setIsColumnSelectorOpen((open) => !open)}
                                aria-expanded={isColumnSelectorOpen}
                                aria-haspopup="true"
                            >
                                <Icon name={isColumnSelectorOpen ? 'columnsFill' : 'columns'} size={16} />
                                Customize columns
                            </button>
                            {isColumnSelectorOpen && (
                                <div className="audience-column-dropdown">
                                    <div className="audience-column-dropdown-header">
                                        <span>Show columns</span>
                                        <button type="button" className="audience-column-reset" onClick={resetTrafficColumns}>
                                            Reset
                                        </button>
                                    </div>
                                    {trafficColumns.map((column, index) => {
                                        const previous = trafficColumns[index - 1];
                                        const showFunnelHeading = column.kind === 'funnel'
                                            && previous?.kind !== 'funnel'
                                            && previous?.kind !== 'funnelRate';
                                        return (
                                            <Fragment key={column.id}>
                                                {showFunnelHeading && (
                                                    <p className="audience-column-group">Funnel steps</p>
                                                )}
                                                <label className="audience-column-option">
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(visibleTrafficColumns[column.id])}
                                                        onChange={() => toggleTrafficColumn(column.id)}
                                                    />
                                                    {column.label}
                                                </label>
                                            </Fragment>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                    {trafficFetching && (
                        <p className="audience-list-loading" role="status">Loading…</p>
                    )}
                    {trafficError && (
                        <p className="audience-status audience-status--error">Could not load traffic sources.</p>
                    )}
                    {!trafficFetching && !trafficError && trafficRows.length === 0 && (
                        <p className="audience-status">
                            {trafficBreakdown === 'device'
                                ? 'No device data in this period yet.'
                                : 'No traffic in this period yet.'}
                        </p>
                    )}
                    {trafficRows.length > 0 && (
                        <div className="audience-traffic-table-wrap">
                            <table className="audience-traffic-table">
                                <thead>
                                    <tr>
                                        <th>{trafficBreakdown === 'device' ? 'Device' : 'Source'}</th>
                                        {visibleTrafficColumnList.map((column) => (
                                            <th key={column.id}>{renderTrafficHeaderLabel(column.label)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {trafficRows.map((source) => (
                                        <tr key={source.id}>
                                            <td className="audience-traffic-source">
                                                <span className="audience-traffic-source-inner">
                                                    <TrafficSourceLogo sourceId={source.id} name={source.name} />
                                                    {source.name}
                                                </span>
                                            </td>
                                            {visibleTrafficColumnList.map((column) => (
                                                <td key={column.id} className={trafficCellClassName(column, source)}>
                                                    {renderTrafficCell(column, source)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {trafficBreakdown === 'device' && (
                        <p className="audience-traffic-note">
                            Note, device metric tracking began in September 2026.
                        </p>
                    )}
                </div>
            </div>

            <div className="audience-intel-card" ref={intelRef}>
                <div className={`audience-leads-panel${leadsFetching ? ' is-loading' : ''}`}>
                    <div className="audience-leads-header">
                        <p className="body-medium-semibold audience-list-title">Leads</p>
                    </div>
                    {(totalLeads > 0 || leadSearch) && (
                        <input
                            type="search"
                            className="audience-lead-search"
                            placeholder="Search by email, name, or ID"
                            value={leadSearch}
                            onChange={(event) => setLeadSearch(event.target.value)}
                            aria-label="Search leads by email, name, or ID"
                        />
                    )}
                    {leadsFetching && (
                        <p className="audience-list-loading" role="status">Loading…</p>
                    )}
                    {leadsError && (
                        <p className="audience-status audience-status--error">Could not load leads.</p>
                    )}
                    {!leadsFetching && !leadsError && leads.length === 0 && !isSearchingLeads && (
                        <p className="audience-status">
                            {emailsOnly
                                ? 'To track leads, place the tracker code on any pages where your leads enter an email address, such as the checkout page.'
                                : 'No leads yet. Place the tracker code on your pages to start tracking visitors.'}
                        </p>
                    )}
                    {!leadsFetching && !leadsError && leads.length === 0 && isSearchingLeads && (
                        <p className="audience-status">No leads match that search.</p>
                    )}
                    {leads.length > 0 && (
                        <ul className="audience-lead-list">
                            {leads.map((lead) => {
                                const isSelected = selectedLeadId === lead.id;
                                const isConnecting = connectingLeadId === lead.id;
                                const canConnectBoth = !leadHasEmail(lead) && !leadHasName(lead);
                                const otherEmails = (lead.emails || []).slice(1);
                                return (
                                    <li key={lead.id} className="audience-lead-item" data-lead-id={lead.id}>
                                        <button
                                            type="button"
                                            className={`audience-lead-row${isSelected ? ' is-selected' : ''}`}
                                            aria-pressed={isSelected}
                                            onClick={() => setSelectedLeadId((current) => (
                                                current === lead.id ? null : lead.id
                                            ))}
                                        >
                                            <span className="audience-lead-avatar" aria-hidden="true">
                                                {leadInitials(lead)}
                                            </span>
                                            <span className="audience-lead-text">
                                                <span className="audience-lead-email-line">
                                                    <span className="audience-lead-email">{leadLabel(lead)}</span>
                                                    {otherEmails.length > 0 && <LeadEmailsBadge emails={otherEmails} />}
                                                </span>
                                            </span>
                                        </button>
                                        {canConnectBoth && !isConnecting && (
                                            <button
                                                type="button"
                                                className="audience-lead-connect"
                                                onClick={() => {
                                                    setJourneyConnectField(null);
                                                    setConnectingLeadId(lead.id);
                                                }}
                                            >
                                                Connect email or name
                                            </button>
                                        )}
                                        {canConnectBoth && isConnecting && (
                                            <form
                                                className="audience-lead-connect-form"
                                                onSubmit={(event) => handleConnectLead(event, lead.id)}
                                            >
                                                <input
                                                    type="email"
                                                    className="audience-add-email-input"
                                                    value={connectEmail}
                                                    onChange={(event) => setConnectEmail(event.target.value)}
                                                    placeholder="Email"
                                                    aria-label="Email"
                                                    autoFocus
                                                />
                                                <input
                                                    type="text"
                                                    className="audience-add-email-input"
                                                    value={connectName}
                                                    onChange={(event) => setConnectName(event.target.value)}
                                                    placeholder="Name"
                                                    aria-label="Name"
                                                />
                                                <div className="audience-lead-connect-actions">
                                                    <button
                                                        type="submit"
                                                        className="audience-add-email-submit"
                                                        disabled={connectSaving || (!connectEmail.trim() && !connectName.trim())}
                                                    >
                                                        {connectSaving ? 'Sending…' : 'Send'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="audience-lead-connect-cancel"
                                                        onClick={() => setConnectingLeadId(null)}
                                                        disabled={connectSaving}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </form>
                                        )}
                                        {isConnecting && connectError && (
                                            <p className="audience-status audience-status--error">{connectError}</p>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {totalLeadPages > 1 && (
                        <nav className="audience-lead-pagination" aria-label="Leads pagination">
                            <span className="audience-lead-pagination-info">
                                {((currentLeadPage - 1) * LEADS_PER_PAGE + 1).toLocaleString()}–
                                {Math.min(currentLeadPage * LEADS_PER_PAGE, totalLeads).toLocaleString()} of{' '}
                                {totalLeads.toLocaleString()}
                            </span>
                            <div className="audience-lead-pagination-controls">
                                <button
                                    type="button"
                                    className="audience-lead-pagination-btn"
                                    onClick={() => setLeadPage(currentLeadPage - 1)}
                                    disabled={currentLeadPage === 1}
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <span className="audience-lead-pagination-page">
                                    {currentLeadPage.toLocaleString()} / {totalLeadPages.toLocaleString()}
                                </span>
                                <button
                                    type="button"
                                    className="audience-lead-pagination-btn"
                                    onClick={() => setLeadPage(currentLeadPage + 1)}
                                    disabled={currentLeadPage === totalLeadPages}
                                    aria-label="Next page"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </nav>
                    )}
                </div>

                <div className="audience-journey-panel">
                    {!selectedLeadId && (
                        <p className="audience-journey-empty">Select a lead to see their journey.</p>
                    )}
                    {selectedLeadId && journeyFetching && (
                        <p className="audience-journey-empty" role="status">Loading…</p>
                    )}
                    {selectedLeadId && !journeyFetching && journeyError && (
                        <p className="audience-status audience-status--error">Could not load this journey.</p>
                    )}
                    {selectedLeadId && !journeyFetching && !journeyError && (
                        <>
                            <div className="audience-journey-header">
                                <div className="audience-journey-identity">
                                    <div className="audience-journey-avatar" aria-hidden="true">
                                        {leadInitials({ id: selectedLeadId, email: journeyEmail, name: journeyName })}
                                    </div>
                                    <div className="audience-journey-identity-text">
                                        <p className="audience-journey-kicker">Customer journey</p>
                                        <div className="audience-journey-name-row">
                                            {journeyHasName ? (
                                                <p className="audience-journey-name">{journeyName}</p>
                                            ) : journeyConnectField === 'name' ? (
                                                <form
                                                    className="audience-journey-connect-form"
                                                    onSubmit={(event) => handleConnectLead(event, selectedLeadId, 'name')}
                                                >
                                                    <input
                                                        type="text"
                                                        className="audience-add-email-input"
                                                        value={connectName}
                                                        onChange={(event) => setConnectName(event.target.value)}
                                                        placeholder="Name"
                                                        aria-label="Name"
                                                        autoFocus
                                                    />
                                                    <button
                                                        type="submit"
                                                        className="audience-add-email-submit"
                                                        disabled={connectSaving || !connectName.trim()}
                                                    >
                                                        {connectSaving ? 'Sending…' : 'Send'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="audience-lead-connect-cancel"
                                                        onClick={() => setJourneyConnectField(null)}
                                                        disabled={connectSaving}
                                                    >
                                                        Cancel
                                                    </button>
                                                </form>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="audience-journey-connect"
                                                    onClick={() => {
                                                        setConnectingLeadId(null);
                                                        setJourneyConnectField('name');
                                                    }}
                                                >
                                                    Connect name
                                                </button>
                                            )}
                                        </div>
                                        <div className="audience-journey-email-row">
                                            {journeyHasEmail ? (
                                                <p className="audience-journey-email">{journeyEmail}</p>
                                            ) : journeyConnectField === 'email' ? (
                                                journeyEmailForm
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="audience-journey-connect"
                                                    onClick={openJourneyEmailForm}
                                                >
                                                    Connect email
                                                </button>
                                            )}
                                            {journeySource && <SourceLogo source={journeySource} />}
                                        </div>
                                        {visibleJourneyEmails.length > 1 && (
                                            <ul className="audience-journey-email-list" aria-label="Other emails">
                                                {visibleJourneyEmails.slice(1).map((email) => (
                                                    <li key={email} className="audience-journey-email-secondary">{email}</li>
                                                ))}
                                            </ul>
                                        )}
                                        {journeyEmails.length > JOURNEY_EMAILS_COLLAPSED && (
                                            <button
                                                type="button"
                                                className="audience-journey-email-toggle"
                                                onClick={() => setShowAllJourneyEmails((current) => !current)}
                                            >
                                                {showAllJourneyEmails
                                                    ? 'Show fewer emails'
                                                    : `Show all ${journeyEmails.length} emails`}
                                            </button>
                                        )}
                                        {journeyHasEmail && (
                                            <div className="audience-journey-email-row">
                                                {journeyConnectField === 'email' ? (
                                                    journeyEmailForm
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="audience-journey-connect"
                                                        onClick={openJourneyEmailForm}
                                                    >
                                                        Add another email
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {journeyConnectField && connectError && (
                                            <p className="audience-status audience-status--error">{connectError}</p>
                                        )}
                                    </div>
                                </div>
                                {journeyValue > 0 && (
                                    <p className="audience-journey-value">
                                        {formatSaleBadge(journeyValue, journeyCurrency)}
                                    </p>
                                )}
                            </div>

                            {journeyEvents.length === 0 ? (
                                <p className="audience-status">No events for this lead.</p>
                            ) : (
                                <ol className="audience-journey-timeline">
                                    {journeyEvents.map((event, index) => {
                                        const isPurchase = event.type === 'purchase';
                                        const isLast = index === journeyEvents.length - 1;
                                        return (
                                            <li
                                                key={event.id}
                                                className={`audience-journey-step${isPurchase ? ' is-purchase' : ''}`}
                                            >
                                                <div className="audience-journey-rail">
                                                    <span className={`audience-journey-dot${isPurchase ? ' is-check' : ''}`}>
                                                        {isPurchase ? <Check size={12} strokeWidth={3} /> : index + 1}
                                                    </span>
                                                    {!isLast && <span className="audience-journey-line" />}
                                                </div>
                                                <div className="audience-journey-body">
                                                    <p className="audience-journey-meta">
                                                        <span className="audience-journey-time">{formatEventTime(event.timestamp)}</span>
                                                        {event.category ? ` · ${event.category}` : ''}
                                                    </p>
                                                    <p className="audience-journey-title">{event.title}</p>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ol>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Audience;
